import React from 'react';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import lineIcon from '../assets/images/footer/line.svg';
import linkIcon from '../assets/images/footer/link.svg';
import logo from '@/assets/images/common/kotomil-logo.png';
import Tooltip from '@/components/Tooltip';

export type FooterProps = {
    /**
     * ログイン後シェル（固定 BottomNav）時のみ。スマホでフッター下余白をナビ高さ＋セーフエリアに合わせ、
     * フッターとナビの間の空スクロールを減らす。
     */
    reserveMobileBottomNav?: boolean;
};

const Footer: React.FC<FooterProps> = ({ reserveMobileBottomNav = false }) => {
    const { t, i18n } = useTranslation('footer');
    const baseUrl = 'https://kotomil.com';
    const currentYear = new Date().getFullYear();

    const isJapanese = i18n.resolvedLanguage === 'ja' || i18n.language?.startsWith('ja');
    const xAccount = isJapanese ? 'KotomilApp' : 'kotomilAppEn';
    const xFollowUrl = `https://x.com/${xAccount}`;
    const instagramAccount = 'kotomilapp';
    const instagramFollowUrl = `https://www.instagram.com/${instagramAccount}/`;

    type FooterLink = { path: string; label: string };

    const supportLinks = t('links.support', {
        returnObjects: true,
    }) as FooterLink[];
    const legalLinks = t('links.legal', {
        returnObjects: true,
    }) as FooterLink[];

    const tooltipLines = t('service.tooltipLines', {
        returnObjects: true,
    }) as string[];

    const tooltipContent = (
        <div className="flex flex-col gap-2 text-center leading-relaxed">
            <div>
                {tooltipLines.map((line, i) => (
                    <p key={`tooltip-${i}`}>{line}</p>
                ))}
            </div>
        </div>
    );

    const shareMessage = t('share.message');
    const shareHashtags = t('share.hashtags');
    const encodedMessage = encodeURIComponent(shareMessage);
    const encodedUrl = encodeURIComponent(baseUrl);
    const encodedHashtags = encodeURIComponent(shareHashtags);

    const lineShareUrl = `https://line.me/R/msg/text/?${encodedMessage}%20${encodedUrl}`;
    const twitterShareUrl = `https://twitter.com/share?url=${encodedUrl}&text=${encodedMessage}&hashtags=${encodedHashtags}`;

    const [isCopied, setIsCopied] = React.useState(false);

    const copyUrlToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(baseUrl);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 3000);
        } catch (err) {
            console.error('URLのコピーに失敗しました:', err);
            const textArea = document.createElement('textarea');
            textArea.value = baseUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 3000);
        }
    };

    return (
        <footer
            className={clsx(
                'mt-auto w-full border-t border-gray-200 bg-gray-50 pt-12 text-gray-800',
                reserveMobileBottomNav
                    ? 'pb-8 max-lg:pb-[calc(var(--kotomil-bottom-nav-height,6rem)+env(safe-area-inset-bottom,0px)+0.75rem)]'
                    : 'pb-8',
            )}
        >
            <div className="max-w-6xl mx-auto px-8">
                <div className="flex flex-col-reverse md:flex-row gap-10 mb-10">
                    {/* Left Section: Brand & Social */}
                    <div className="flex flex-col items-center justify-center gap-2 md:w-4/12 border-t md:border-t-0 md:border-r md:border-gray-200 pt-6 md:pt-0 md:pr-10">
                        {/* Logo & Catchphrase */}
                        <Tooltip content={tooltipContent}>
                            <div className="flex flex-col items-center gap-3 text-center p-4 rounded-xl transition-colors duration-300 hover:bg-gray-100 cursor-default">
                                {/* Slogan */}
                                <p className="text-gray-900 text-xs font-bold tracking-widest leading-relaxed">
                                    <Trans
                                        i18nKey="service.catchphrase"
                                        ns="footer"
                                        components={{ br: <br /> }}
                                    />
                                </p>

                                <div className="flex items-center gap-3 w-fit">
                                    <img src={logo} alt="Kotomil" className="h-8 w-auto" />
                                    <span className="text-2xl font-bold tracking-wider text-gray-900">
                                        Kotomil
                                    </span>
                                </div>
                            </div>
                        </Tooltip>

                        {/* Social Links (Icon Only) */}
                        <div className="flex gap-3">
                            <Tooltip content="X (Twitter)">
                                <a
                                    href={xFollowUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-black hover:text-white hover:border-black transition-all duration-300 shadow-sm"
                                    aria-label="X (Twitter)"
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                    </svg>
                                </a>
                            </Tooltip>
                            <Tooltip content="Instagram">
                                <a
                                    href={instagramFollowUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 hover:text-white hover:border-transparent transition-all duration-300 shadow-sm"
                                    aria-label="Instagram"
                                >
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                    </svg>
                                </a>
                            </Tooltip>
                        </div>
                    </div>

                    {/* Right Section: Links */}
                    <div className="flex flex-col md:flex-row md:flex-wrap gap-8 md:gap-12 md:w-8/12 md:justify-around w-full text-left">
                        {/* Support Links */}
                        <div className="flex flex-col items-start">
                            <h4 className="text-gray-900 font-bold tracking-wider mb-4 text-sm pb-2 inline-block min-w-[80px]">
                                {t('sections.support.title')}
                            </h4>
                            <ul className="space-y-3">
                                {supportLinks.map((link) => (
                                    <li key={link.path}>
                                        <Link
                                            to={link.path}
                                            className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium hover:underline"
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Legal Links */}
                        <div className="flex flex-col items-start">
                            <h4 className="text-gray-900 font-bold tracking-wider mb-4 text-sm pb-2 inline-block min-w-[80px]">
                                {t('sections.legal.title')}
                            </h4>
                            <ul className="space-y-3">
                                {legalLinks.map((link) => (
                                    <li key={link.path}>
                                        <Link
                                            to={link.path}
                                            className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium hover:underline"
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Share Links */}
                        <div className="flex flex-col items-start">
                            <h4 className="text-gray-900 font-bold tracking-wider mb-4 text-sm pb-2 inline-block min-w-[80px]">
                                {t('share.title')}
                            </h4>
                            <ul className="space-y-3">
                                <li>
                                    <button
                                        onClick={copyUrlToClipboard}
                                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all px-2 py-1 rounded-md text-sm font-medium group w-fit"
                                    >
                                        {isCopied ? (
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="text-green-600"
                                            >
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        ) : (
                                            <img
                                                src={linkIcon}
                                                width="14"
                                                className="opacity-60 group-hover:opacity-100 transition-opacity"
                                                alt=""
                                            />
                                        )}
                                        <span className={isCopied ? 'text-green-600' : ''}>
                                            {isCopied
                                                ? t('share.copiedLabel')
                                                : t('share.copyLabel')}
                                        </span>
                                    </button>
                                </li>
                                <li>
                                    <a
                                        href={twitterShareUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all px-2 py-1 rounded-md text-sm font-medium w-fit"
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                        >
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                        </svg>
                                        {t('share.twitterLabel')}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href={lineShareUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all px-2 py-1 rounded-md text-sm font-medium w-fit"
                                    >
                                        <img src={lineIcon} width="14" alt="LINE" />
                                        {t('share.lineLabel')}
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Copyright */}
                <div className="border-t border-gray-200 pt-6 text-center">
                    <p className="text-xs text-gray-500 font-medium">
                        {t('copyright', { year: currentYear })}
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
